import { ArrayUtil } from "./ArrayUtil";
import { ModelRow } from "./ModelRow";
import { ObjectUtil } from "./ObjectUtil";
import { OrderByInfo } from "./OrderByInfo";
import { StringUtil } from "./StringUtil";
import { UrlUtil } from "./UrlUtil";

interface ModelSearchFilterProps {
    parentLink: any;
    subscribeToChanges?: boolean;
    maxSearchResults?: number;
    orderBy?: OrderByInfo[];
    fieldList?: string | object;
    data?: any;
}

/**
 * ModelSearchFilter is used to create filter objects that are suitable for Model.search requests.
 */
export class ModelSearchFilter implements ModelSearchFilterProps {
    private _parentLink: any;
    private _subscribeToChanges: boolean;
    private _maxSearchResults: number;
    private _orderBy: OrderByInfo[];
    private _fieldList: object;
    private _data: any;

    public constructor(props?: Partial<ModelSearchFilterProps>) {
        Object.assign(this, props);
        this._data ??= {};
    }

    public static from(filter?: any): ModelSearchFilter {
        if (filter instanceof ModelSearchFilter) {
            return filter;
        } else {
            return new ModelSearchFilter({ data: filter });
        }
    }

    public static resolveData(filter?: any): any {
        if (!ObjectUtil.isEmptyObject(filter) && ObjectUtil.isObject(filter)) {
            if (typeof filter.toJSON === 'function') {
                const result = filter.toJSON();
                if (filter instanceof ModelRow) {
                    delete result._appending;
                }
                return result ?? {};
            } else {
                return { ...filter };
            }
        } else {
            return {};
        }
    }

    public get parentLink(): any {
        return this._parentLink;
    }

    public set parentLink(value: any) {
        this._parentLink = value;
    }

    public get subscribeToChanges(): boolean {
        return this._subscribeToChanges;
    }

    public set subscribeToChanges(value: boolean) {
        this._subscribeToChanges = value;
    }

    public get maxSearchResults(): number {
        return this._maxSearchResults;
    }

    public set maxSearchResults(value: number) {
        this._maxSearchResults = value;
    }

    public get orderBy(): OrderByInfo[] {
        return this._orderBy;
    }

    public set orderBy(value: OrderByInfo[]) {
        this._orderBy = ArrayUtil.isEmptyArray(value) ? null : value;
    }

    public get fieldList(): object {
        return this._fieldList;
    }

    public set fieldList(value: string | object) {
        if (this._fieldList !== value) {
            this._fieldList = this.resolveFieldList(value);
        }
    }

    private resolveFieldList(value: string | object): object {
        let result: any = value;
        if (typeof value === "string") {
            result = StringUtil.isEmptyString(value) ? null : { layoutName: value };
        }
        if (ObjectUtil.isObject(result) && !ObjectUtil.isEmptyObject(result)) {
            const layoutId = UrlUtil.getSearchParamFromUrl("layoutId");
            if (layoutId != null) {
                result.layoutId = layoutId;
            }
            return result;
        }
        return null;
    }

    public get data(): any {
        return this._data;
    }

    public set data(value: any) {
        this._data = ModelSearchFilter.resolveData(value);
    }

    /**
     * Sets a field in the filter data.
     * @param field - The field name to set
     * @param value - The value to assign (can be a function that returns the value)
     * @param condition - If false, the field is deleted regardless of value
     * @returns This ModelSearchFilter instance
     */
    public set(field: string, value: any, condition: boolean = true): ModelSearchFilter {
        this.internalSet(this.data, field, value, condition);
        return this;
    }

    private internalSet(target: any, field: string, value: any, condition: boolean = true) {
        if (field == null) {
            return;
        }
        const resolvedValue = condition ? (typeof value === 'function' ? value() : value) : null;
        if (resolvedValue != null) {
            target[field] = resolvedValue;
        } else {
            delete target[field];
        }
    }

    public toJSON(): any {
        let result: any = {};
        this.internalSet(result, "_field_list", this.fieldList);
        this.internalSet(result, "_max_search_results", this.maxSearchResults, this.maxSearchResults > 0);
        this.internalSet(result, "_order_by", this.orderBy?.map(info => `${info.field} ${info.sort}`));
        this.internalSet(result, "_parent_link", this.parentLink);
        this.internalSet(result, "_subscribe_to_changes", this.subscribeToChanges, this.subscribeToChanges);
        result = { ...result, ...this.data};
        return ObjectUtil.isEmptyObject(result, true) ? undefined : result;
    }

    public getSearchBody(): string | undefined {
        return JSON.stringify(this.toJSON());
    }

    /**
    * Returns true if the filter contains at least one non-null data property,
    * excluding properties like _order_by, _max_search_results, etc.
    */
    public hasSearchCriteria(): boolean {
        return !ObjectUtil.isEmptyObject(this.data, true);
    }
}
